import React, {Component, Fragment} from "react";
import {NavLink, Redirect, Route} from "react-router-dom";
import InfiniteScroll from 'react-infinite-scroller';
import PropTypes from 'prop-types';
import FallbackImage from "react-image-fallback";
import * as moment from "moment";
import DatePicker from 'react-datepicker';

import {clamp, formatPrice, mapObjectToArray, objLength, testPath} from "../../lib/tools";
import {PopupConsumer} from "../popups";
import {reduxWrapper} from "../../globalState";
import {FormContext, RouteContext} from "../../contexts";
//Icons
import tick from "../../resources/icons/tick.svg";
import festenLogo from "../../resources/icons/logo-green.svg";
import BinIcon from "../../resources/icons/bin-darkGrey.svg";
import PlusCircleIcon from "../../resources/icons/plusCircle.svg";
import MinusCircleIcon from "../../resources/icons/minusCircle.svg";
import SearchIcon from "../../resources/icons/magnifying-glass.svg";
//Images
import placeholderImage from "../../resources/images/placeholder.png";
import {getImage} from "../../lib/api";
import {FormInput} from "./formValidation";
import arrowDown from "../../resources/icons/drop-darkGrey.svg";


//Displays the logo
export const Logo = ({className, onClick}) => (
	<img onClick={onClick} className={className} src={festenLogo} alt="Logo"/>
);

Logo.propTypes = {
	className: PropTypes.string
};


//<editor-fold desc="Text Wrappers">
export const CloseSubSection = ({className}) => (
	<PopupConsumer>
		{({loginPrompt, createPopup, userId}) => (
			<Button
				className={["closeSubBtn", className].join(" ")}
				to="/min-fest"
				onClick={() => !userId && !loginPrompt && createPopup({preset: "loginPrompt"})}
			/>
		)}
	</PopupConsumer>
);

//A header for text with an icon
export const IconTitle = ({iconLeft, iconRight, text}) => (
	<div className={iconLeft ? "iconLeftTitle" : "iconRightTitle"}>
		<img src={iconLeft || iconRight} alt={text}/>
		<h3>{text}</h3>
	</div>
);

IconTitle.propTypes = {
	text: PropTypes.string.isRequired,
	iconLeft: PropTypes.string,
	iconRight: PropTypes.string
};


//A subheader
export const H2 = ({children}) => (
	<div className="subSectionTitle">
		<h2>{children}</h2>
	</div>
);

H2.propTypes = {
	children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired
};
//</editor-fold>

//<editor-fold desc="Cart item">
//Single product rendered as a cart item
class CartItemRender extends React.Component {
	constructor(props) {
		super(props);
		this.cached = false;
	}
	
	render() {
		const {updateCart, attributes, options, deleteCart, product: {id, product: {name, full, thumbnail: thumb, qty: maxQty}, qty, price, config}} = this.props;
		
		if (!full) {
			return (
				<div className="product">
					<LoadingSpinner/>
				</div>
			);
		}
		
		const thumbnail = (config && config.imageId) || thumb;
		
		return (
			<div className="product">
				
				<div className="prodImgWrapper">
					<Image src={getImage(thumbnail)} alt="produkt"/>
				</div>
				
				<div className="productSettings">

					<div className="productName">
						<span>{name}</span>
						<img src={BinIcon} alt="slet" onClick={() => deleteCart({id})}/>
					</div>

					<div className="productInfo">
						{config && mapObjectToArray(config.options, (x, i) =>
							<p key={i}><span>{attributes[x.attributeId]}:</span> {options[x.optionId]}</p>
						)}
					</div>

					<div className="productPrice">
						<QtySelector value={qty} max={maxQty} onChange={val => updateCart({id, data: {qty: val}})}/>
						<span className="price">{formatPrice(parseInt(qty, 10) * price)}</span>
					</div>

				</div>
			 
			</div>
		);
	}
}

export const CartItem = reduxWrapper("deleteCart", "updateCart", "loadAttributes", "loadOptions")(CartItemRender);

// Quantity selector with +, - and input options 
export const QtySelector = ({value, onChange, max = 99, label = "Antal", min = 1}) => (
	<div className="qtySelector">
		<span>{label}: </span>
		<Button className="decrease" icon={MinusCircleIcon} onClick={() => onChange(clamp(value - 1, min, max))}/>
		<input type="number" value={value}
		       onKeyDown={e => e.key === "Enter" && e.currentTarget.blur()}
		       onChange={e => onChange(e.target.value)}
		       onBlur={() => onChange(clamp(value, min, max))}/>
		<Button className="increase" icon={PlusCircleIcon} onClick={() => onChange(clamp(value + 1, min, max))}/>
	</div>
);

QtySelector.propTypes = {
	value: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
	max: PropTypes.number
};
//</editor-fold>

//<editor-fold desc="Buttons">
//A button component
//Used for all buttons in app, can be text, icon, text and icon and link.
export class Button extends Component {
	static propTypes = {
		className: PropTypes.string,
		icon: PropTypes.string
	};
	
	static contextType = FormContext;
	
	render() {
		const {className, id, icon, to, noFocus, disable, innerRef, blank, children, submit, ...props} = this.props;
		
		const onClick = submit ? this.context.onSubmit : props.onClick;
		
		let classes = [
			"btn"
		];
		(!icon && children) && classes.push("btnText");
		(icon && !children) && classes.push("btnIcon");
		(icon && children) && classes.push("btnIconText");
		className && classes.push(className);
		if (to) {
			return (
				<NavLink
					onMouseDown={e => noFocus && e.preventDefault()}
					to={to}
					onClick={disable ? null : onClick}
					innerRef={innerRef}
					target={blank ? "_blank" : ""}
					className={classes.join(" ")}
				>
					<StandardBtn children={children} icon={icon} {...props}/>
				</NavLink>
			)
		} else {
			return (
				<div
					onMouseDown={e => noFocus && e.preventDefault()}
					onClick={disable ? null : onClick}
					ref={innerRef}
					className={classes.join(" ")}
					id={id}
				>
					<StandardBtn children={children} icon={icon} {...props}/>
				</div>
			)
		}
	}
}

//A base button
const StandardBtn = ({icon, children, style}) => {
	if (icon && !children) {
		return (
			<Fragment>
				<div className="btnIcon">
					<img src={icon} alt="knap" style={style}/>
				</div>
			</Fragment>
		)
	} else {
		return (
			<Fragment>
				{icon && <img src={icon} alt="knap"/>}
				{children}
			</Fragment>
		)
	}
};

StandardBtn.propTypes = {
	noFocus: PropTypes.bool,
	to: PropTypes.string,
	onClick: PropTypes.func,
	disable: PropTypes.bool,
	blank: PropTypes.bool,
	icon: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	innerRef: PropTypes.func
};
//</editor-fold>

//<editor-fold desc="Form Elements">
export const HiddenSubmit = () => <input type="submit" style={{display: "none"}}/>;

export const TextField = ({onChange, type, name, value, placeholder, icon}) => (
	<input
		onChange={e => onChange(e.target.value, e.target.name)}
		type={type || "text"}
		name={name}
		value={value}
		placeholder={placeholder}
		style={{backgroundImage: `url(${icon})`}}
	/>
);

export const SimpleTextField = ({className, error, onChange, type, name, value, placeholder, title, icon}) => (
	<label className={["textInputWrapper", className, error ? "error" : ""].join(" ")}>
		{title}
		<TextField onChange={onChange} type={type} name={name} value={value} placeholder={placeholder} icon={icon}/>
		{error && <span className="errorText">{error}</span>}
	</label>
);

export class SearchField extends Component {
	state = {
		value: ""
	};
	
	constructor(props) {
		super(props);
		
		this.onChange = (e) => {
			const val = e.target.value;
			this.setState({value: val});
			this.props.onChange && this.props.onChange(val);
		};
		
		this.onEnter = e => {
			if (e.key === "Enter") {
				e.preventDefault();
				this.props.onSubmit && this.props.onSubmit(e.target.value);
			}
		};
	}
	
	
	render() {
		const {className, onSubmit} = this.props;
		const value = this.props.hasOwnProperty("value") ? this.props.value : this.state.value;
		
		return (
			<input
				className={["searchField", className].join(" ")}
				type="search"
				value={value}
				onChange={this.onChange}
				ref={element => (element || {}).onsearch = e => onSubmit && onSubmit(e.target.value)}
				placeholder="Søg"
				onKeyDown={this.onEnter}
				style={{backgroundImage: `url(${SearchIcon})`}}
			/>
		);
	}
}

export const RadioGroup = ({value, onChange, radios}) => (
	<div className="radioGroup">
		{mapObjectToArray(radios, (x, i) => <RadioButton key={i} id={i} value={value} onChange={onChange} {...x}/>)}
	</div>
);

export const RadioButton = ({id, value, onChange, icon, className, label, title, price}) => (
	<label>
		<div className={["customRadio", value === id ? "active" : "inactive", className].join(" ")}
		     onClick={() => onChange(id)}>
			<img src={icon || tick} alt="checkbox" className={icon == null ? "tick" : "icon"}/>
		</div>
		{label}
		{title && <span>{title}</span>}
		{price && <span className="price">{price}</span>}
	</label>
);
//</editor-fold>

export const LoadingSpinner = () => (
	<div className="dotLoading">
		<div className="dot"/>
		<div className="dot"/>
		<div className="dot"/>
	</div>
);


//A button for downloading a file, used in the guest list
export const DownloadLink = ({icon, children, href}) => (
	<a className="downloadLink" href={href} download>
		{icon && <img src={icon} alt={children}/>}
		{children && <span>{children}</span>}
	</a>
);

DownloadLink.propTypes = {
	icon: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	href: PropTypes.string.isRequired
};


//A field for file uploads used in the guest list
export class UploadField extends React.Component {
	constructor(props) {
		super(props);
		
		this.state = {file: null};
		this.onUpload = this.onUpload.bind(this);
		this.onSelect = this.onSelect.bind(this);
	}
	
	onSelect(e) {
		let file = e ? e.target.files[0] : null;
		this.setState({file: file});
		if (this.props.instant)
			this.onUpload();
		if (file == null)
			this.upload.value = "";
		if (this.props.onSelect)
			this.props.onSelect(file);
	}
	
	onUpload() {
		if (this.props.onUpload)
			this.props.onUpload(this.state.file);
		this.setState({file: null});
		this.upload.value = "";
	}
	
	render() {
		const {template, instant} = this.props;
		
		return (
			<div className="uploadField">
				<span>{this.props.title}</span>
				
				{(!instant && this.state.file) &&
				<div className="fileToUpload">
							<span className="fileName">
								{this.state.file && this.state.file.name}
							</span>
					{this.state.file && <div className="reset" onClick={() => this.onSelect(null)}>X</div>}
				</div>
				}
				
				
				{template && <DownloadLink href={template}>Hent skabalon</DownloadLink>}
				
				<Button className="btnGreen ga-importGuests"
				        onClick={() => this.state.file ? this.onUpload() : this.upload.click()}>
					{this.state.file ? "Importer fil" : "Vælg"}
				</Button>
				<input type="file" ref={r => this.upload = r} onChange={this.onSelect} accept={this.props.accept}/>
			</div>
		);
	}
}

UploadField.propTypes = {
	accept: PropTypes.string,
	instant: PropTypes.bool,
	onSelect: PropTypes.func,
	onUpload: PropTypes.func.isRequired,
	template: PropTypes.string,
	title: PropTypes.string.isRequired
};

export class FileSelect extends React.Component {
	state = {
		file: null
	};
	
    constructor(props) {
        super(props);
        
        this.onClick = () => {
            if (this.state.file) {
                if (this.props.onUpload) {
	                this.props.onUpload(this.state.file);
	                this.upload.value = "";
	                this.setState({file: null});
                }
            } else {
            	this.upload.click();
            }
        };
        
        this.onSelect = e => {
	        let file = e ? e.target.files[0] : null;
	        this.setState({file: file});
	        
	        if (this.props.instant)
		        this.onClick();
	        
	        if (file == null) this.upload.value = "";
	        
	        this.props.onSelect && this.props.onSelect(file);
        };
    }
    
    render() {
        return (
            <div className="fileSelect">
	            <p className="fileName">{this.state.file ? this.state.file.name : "Ingen fil valgt"}</p>
	            <Button className="btnGreen" onClick={this.onClick}>
		            {this.state.file && this.props.hasOwnProperty("onUpload") ? "Upload" : "Vælg"}
	            </Button>
	            <input type="file" ref={r => this.upload = r} onChange={this.onSelect} accept={this.props.accept} style={{display: "none"}}/>
            </div>
        );
    }
}

//An override for infiniteScroll setting #content as the scrollable container
export class ContentInfiniteScroll extends InfiniteScroll {
	getParentElement(el) {
		if (this.props.scrollParent) {
			return this.props.scrollParent;
		}
		return document.getElementById(this.props.parent || "content");
	}
}

export class InfinityScroll extends React.Component {
	static propTypes = {
		loadMore: PropTypes.func,
		loadCount: PropTypes.number
	};
	
	mounted = true;
	
	constructor(props) {
		super(props);
		
		this.loadKey = null;
		
		this.loadMore = (page) => {
			this.setState({loading: true, hasMore: false});
			this.props.loadMore(page, this.props.loadCount)
				.then(amount => this.mounted && this.setState({
					loading: false,
					hasMore: amount >= this.props.loadCount
				}))
		}
	}
	
	componentWillUnmount() {
		this.mounted = false;
	}
	
	state = {
		hasMore: true,
		loading: false
	};
	
	render() {
		const {children, className, parent, loadKey} = this.props;
		
		if (loadKey !== this.loadKey) {
			this.loadKey = loadKey;
			if (!this.state.hasMore)
				this.setState({hasMore: true});
		}
		
		return (
			<ContentInfiniteScroll className={className} pageStart={0} loadMore={this.loadMore}
			                       hasMore={this.state.hasMore}
			                       useWindow={false} parent={parent}>
				{children}
				{this.state.loading && <LoadingSpinner/>}
			</ContentInfiniteScroll>
		);
	}
}

//Rendering for a button version of the date picker
export class DatePickerButtonRender extends Component {
	render() {
		const {onClick, value, fakePlaceholder} = this.props;
		return (
			<Button className="datepickerButton" onClick={onClick}>
				{
					!value ? <span className="fakePlaceholder">{fakePlaceholder}</span> :
						value
				}
			</Button>
		);
	}
}

DatePickerButtonRender.propTypes = {
	onClick: PropTypes.func,
	value: PropTypes.any,
	fakePlaceholder: PropTypes.string
};

//A button version of the date picker
export const DatePickerButton = ({selected, ...props}) => (
	<DatePicker {...props}
	            selected={selected ? (moment.isMoment(selected) ? selected : moment(selected)) : null}
	            customInput={<DatePickerButtonRender fakePlaceholder={props.fakePlaceholder}/>}/>
);

export const Image = (props) => <FallbackImage {...props} fallbackImage={placeholderImage}
                                               initialImage={<LoadingSpinner/>}/>;


//Wrapper for realative routing
export class RelativeRoute extends React.Component {
	static contextType = RouteContext;
	
	render() {
		const {path, ...props} = this.props;
		
		const newRoute = path ?
			"/" + (this.context.route + "/" + path.replace(/(?:^\/*|\/*$)/g, "")).replace(/^\/*/g, "") :
			this.context.route;
		
		return (
			<RouteContext.Provider value={{route: newRoute}}>
				<Route {...props} path={newRoute}/>
			</RouteContext.Provider>
		);
	}
}

// Switch wrapper for relative routing
export class RelativeSwitch extends React.Component {
	
	static contextType = RouteContext;
	
	static propTypes = {
		children: PropTypes.node
	};
	
	render() {
		const {children} = this.props;
		let newChildren = [];
		
		let match = null;
		React.Children.forEach(children, (child, index) => {
			if (!React.isValidElement(child)) return;
			
			if (child.type !== Route) {
				if (child.type === Redirect) {
					if (child.props.from && !testPath(child.props.from, {exact: true}))
						return;
				}
				
				newChildren.push(React.cloneElement(child, {key: index}));
				
				return;
			} else if (match)
				return;
			
			const {path, ...props} = child.props;
			const newRoute = path ?
				"/" + (this.context.route + "/" + path.replace(/(?:^\/*|\/*$)/g, "")).replace(/^\/*/g, "") :
				this.context.route;
			
			match = testPath(newRoute, props);
			if (match) {
				newChildren.push(React.cloneElement(child, {
					location: window.location,
					computedMatch: match,
					key: index
				}));
			}
		}, this);
		
		return (
			<RouteContext.Provider value={{route: match ? match.path : this.context.route}}>
				{newChildren}
			</RouteContext.Provider>
		)
	}
}

export class Carousel extends React.Component {
	
	constructor(props) {
		super(props);
		
		this.onChange = (x) => {
			this.props.onChange && this.props.onChange(x);
			this.setState({selection: x});
		}
	}
	
	
	state = {
		selection: 0
	};
	
	render() {
		const {images = []} = this.props;
		let value = this.props.hasOwnProperty("value") ? this.props.value : this.state.selection;
		value = images.includes(value) ? value : images[0] || null;
		return (
			<div className="carouselWrapper">
				<div className="imageContainer">
					<Image src={getImage(value)}/>
				</div>
				{images.length > 1 && <div className="imageList">
					{images.map(x =>
						<Image className={"imagePreview" + (value === x ? " selected" : "")} src={getImage(x)}
						       onClick={() => this.onChange(x)} key={x}/>
					)}
				</div>}
			</div>
		);
	}
}

export const StartTutorial = ({onClick}) => (
	<div className="startTutorial" onClick={() => onClick()}>i</div>	
);

export class PageNav extends React.Component {
 
	state = {
		page: 0
	};
	
	componentWillReceiveProps(nextProps, nextContext) {
		if (nextProps.page !== this.props.page && nextProps.page !== this.state.page) {
			this.setState({page: nextProps.page});
		}
	}
	
	changePage(change) {
		this.setPage(Math.max(this.state.page + parseInt(change, 10), 0));
	}
	
	setPage(page) {
		page = parseInt(page, 10);
		this.setState({page: page});
		this.props.onChange(page);
	}
	
	render() {
    	const {page} = this.state;
    	
        return (
	        <div className="pageNav">
		        <Button onClick={() => this.changePage(-1)}>-1</Button>
		        <FormInput title="" placeholder="" name="ignore" value={page.toString()} onChange={page => this.setState({page})} onCommit={val => this.setPage(val)}/>
		        <Button onClick={() => this.changePage(1)}>+1</Button>
	        </div>
        );
    }
}

export class SortedTable extends React.Component {
	
	static propTypes = {
		columns: PropTypes.object.isRequired,
		rows: PropTypes.array.isRequired,
		loading: PropTypes.any,
		onChange: PropTypes.func,
		redirect: PropTypes.string,
		redirectProp: PropTypes.string,
		history: PropTypes.any
	};
	
	state = {
		order: "id",
		orderAsc: true,
		page: 0
	};
	
	changePage(page) {
		this.setState({page});
		this.props.onChange({page});
	}
	
	render() {
		const {onChange, columns, rows, loading, redirect, redirectProp, pages, values = {}} = this.props;
		
		const page = values.hasOwnProperty("page") ? values.page || 0 : this.state.page;
		const order = values.hasOwnProperty("page") ? values.order : this.state.order;
		const orderAsc = values.hasOwnProperty("orderAsc") ? values.orderAsc : this.state.orderAsc;
		
		const headerClick = (col) => {
			const column = this.props.columns[col];
			if (!column || !column.sortable) return;
			let newState;
			
			if (order === col)
				newState = {order: col, orderAsc: !orderAsc};
			else
				newState = {order: col, orderAsc: true};
			
			this.setState(newState);
			onChange(newState);
		};
		
		return (
			<div className="sortedTable">
				{pages !== false ? <PageNav page={page} onChange={val => this.changePage(val)}/> : null}
				<table className="festenTable">
					<thead>
					<tr>
						{mapObjectToArray(columns, (x, k) => (
							<th className={x.sortable ? "sortable" : ""} onClick={() => headerClick(k)} key={k}>
								{x.name}
								{order !== k ? null :
									<img src={arrowDown} alt="chevron" className={orderAsc ? "flipped" : ""}/>
								}
							</th>
						))}
					</tr>
					</thead>
					<tbody>
					{loading ?
						<tr>
							<td colSpan={objLength(columns)}><LoadingSpinner/></td>
						</tr> :
						rows.map((row, i) => (
							<tr onClick={() => redirect && redirectProp && this.props.history.push(redirect + row[redirectProp])}
							    key={redirectProp ? row[redirectProp] : i}>
								{mapObjectToArray(columns, (col, k) => (
									<td key={k} className={redirect ? "clickable" : ""}>{col.render ? col.render(row[col.propName || k], row, col) : row[col.propName || k]}</td>
								))}
							</tr>
						))
					}
					</tbody>
				</table>
				{pages !== false ? <PageNav page={page} onChange={val => this.changePage(val)}/> : null}
			</div>
		);
	}
}

export const SvgCross = props => (
	<div className="cross" {...props}>
		<svg version="1.1" id="Capa_1" x="0px" y="0px" viewBox="0 0 348.333 348.334" >
			<g>
				<path d="M336.559,68.611L231.016,174.165l105.543,105.549c15.699,15.705,15.699,41.145,0,56.85   c-7.844,7.844-18.128,11.769-28.407,11.769c-10.296,0-20.581-3.919-28.419-11.769L174.167,231.003L68.609,336.563   c-7.843,7.844-18.128,11.769-28.416,11.769c-10.285,0-20.563-3.919-28.413-11.769c-15.699-15.698-15.699-41.139,0-56.85   l105.54-105.549L11.774,68.611c-15.699-15.699-15.699-41.145,0-56.844c15.696-15.687,41.127-15.687,56.829,0l105.563,105.554   L279.721,11.767c15.705-15.687,41.139-15.687,56.832,0C352.258,27.466,352.258,52.912,336.559,68.611z"/>
			</g>
		</svg>
	</div>
);



// WEBPACK FOOTER //
// ./src/components/common/common.js